import { useIntl } from 'react-intl';
import { ButtonUI, Selector, TaptapList } from 'sonata-react-components';
import { formatDate } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getFormattedCountries } from '@/helpers/country.helper';
import LQIMetricsTooltip from '../LQIMetricsTooltip/LQIMetricsTooltip';

import './SelectedIndexLQI.scss';

type Props = {
  onEdit: () => void;
  onRemove: () => void;
  selectedIndex: Record<string, unknown>;
};

type SelectedLQIItem = {
  id: string;
  name: string;
  countries: string;
  version: string;
  created: string;
  metrics: Record<string, unknown>[] | null;
  threshold: string;
  actions: unknown[];
};

type ThresholdOption = { id: string; label: string };

function SelectedIndexLQI({ onEdit, onRemove, selectedIndex }: Props) {
  const { formatMessage } = useIntl();
  const { setProp, targeting } = useTargetingContext();
  const { lqi } = targeting;

  const thresholdOptions: ThresholdOption[] = [
    {
      id: '16.5',
      label: formatMessage({
        defaultMessage: 'Low Threshold - 16.5%',
        id: 'targeting.lqi.index.theshold.low'
      })
    },
    {
      id: '49.5',
      label: formatMessage({
        defaultMessage: 'Medium Threshold - 49.5%',
        id: 'targeting.lqi.index.theshold.medium'
      })
    },
    {
      id: '82.5',
      label: formatMessage({
        defaultMessage: 'Advance Threshold - 82%',
        id: 'targeting.lqi.index.theshold.advance'
      })
    }
  ];

  const defaultOption = thresholdOptions.find(({ id }) => parseFloat(id) === lqi?.prebid?.threshold);

  const handleSetPreBidThreshold = (option: ThresholdOption) => {
    setProp('lqi', {
      ...lqi,
      prebid: {
        ...lqi?.prebid,
        threshold: parseFloat(option.id)
      }
    });
  };

  const dataSource = selectedIndex.dataSource as { metrics?: Record<string, unknown>[] } | undefined;

  const compositionData: SelectedLQIItem[] = [
    {
      actions: [],
      countries: getFormattedCountries(selectedIndex.countries as string[]).join(', '),
      created: formatDate(selectedIndex.created as string, 'dayOf'),
      id: String(selectedIndex._id ?? selectedIndex.id ?? 'lqi-selected'),
      metrics: dataSource?.metrics ?? null,
      name: String(selectedIndex.name ?? ''),
      threshold: '',
      version: String(selectedIndex.version ?? '')
    }
  ];

  return (
    <div className="selectedIndexLQI">
      <TaptapList className="selectedIndexLQI__list" data={compositionData} id="selectedIndexLQI-list">
        <TaptapList.ColumnsBar>
          <TaptapList.ColumnsBar.Column
            className="name"
            description={formatMessage({ defaultMessage: 'Name', id: 'targeting.lqi.index.col.name' })}
            id="name"
          />
          <TaptapList.ColumnsBar.Column
            className="countries"
            description={formatMessage({ defaultMessage: 'Countries', id: 'targeting.lqi.index.col.countries' })}
            id="countries"
          />
          <TaptapList.ColumnsBar.Column
            className="version"
            description={formatMessage({ defaultMessage: 'Version', id: 'targeting.lqi.index.col.version' })}
            id="version"
          />
          <TaptapList.ColumnsBar.Column
            className="created"
            description={formatMessage({ defaultMessage: 'Created on', id: 'targeting.lqi.index.col.created' })}
            id="created"
          />
          <TaptapList.ColumnsBar.Column
            className="metrics"
            description={formatMessage({ defaultMessage: 'Metrics used', id: 'targeting.lqi.index.col.metrics' })}
            id="metrics"
          />
          <TaptapList.ColumnsBar.Column
            className="threshold"
            description={formatMessage({ defaultMessage: 'Threshold', id: 'targeting.lqi.index.col.threshold' })}
            id="threshold"
          />
          <TaptapList.ColumnsBar.Column className="actions" description="" id="actions" />
        </TaptapList.ColumnsBar>
        <TaptapList.RowsList<SelectedLQIItem> emptyStateIcon="fal fa-bolt" emptyStateTitle="">
          {itemsList =>
            itemsList.map(item => (
              <TaptapList.RowsList.Row id={item.id} key={item.id}>
                <span className="name">{item.name}</span>
                <span className="countries">{item.countries}</span>
                <span className="version">{item.version}</span>
                <span className="created">{item.created}</span>
                <span className="metrics">{item.metrics && <LQIMetricsTooltip metrics={item.metrics} />}</span>
                <span className="threshold">
                  <Selector
                    {...({ menuPosition: 'fixed' } as object)}
                    id="lqiIndex-threshold"
                    onChange={handleSetPreBidThreshold}
                    options={thresholdOptions}
                    tiny
                    type="simple"
                    value={defaultOption ? [defaultOption] : undefined}
                  />
                </span>
                <span className="actions">
                  <ButtonUI
                    icon="fa-light fa-pen-to-square"
                    onClick={onEdit}
                    shape="link"
                    size="ultraSmall"
                    type="secondary"
                  />
                  <ButtonUI
                    icon="fa-light fa-trash"
                    onClick={onRemove}
                    shape="link"
                    size="ultraSmall"
                    type="secondary"
                  />
                </span>
              </TaptapList.RowsList.Row>
            ))
          }
        </TaptapList.RowsList>
      </TaptapList>
    </div>
  );
}

export default SelectedIndexLQI;
